// Central place to define the feature flag names used in Unleash and config
export const SESSION_END_ORDER_ACTION_CHANGES_FLAG_NAME =
  'session_end_order_action_changes';
export const TIMER_RESET_ON_CART_UPDATE_FLAG_NAME =
  'timer_reset_on_cart_update';
export const SUPPRESS_UPSELL_FLAG_NAME = 'suppress_upsell';
export const DO_NOT_UPSELL_IN_EXPRESS_MODE_FLAG_NAME =
  'do_not_upsell_in_express_mode';
export const UNRESOLVED_ITEMS_FLAG_NAME = 'unresolved_items';
export const HITL_3_0_FLAG_NAME = 'hitl_3_0';
export const AGENT_CART_FLAG_NAME = 'agent_cart';
export const HITL_EXPRESS_TIMER_FLAG_NAME = 'hitl_express_timer';
// Carries a variant whose payload value is the agent response-time SLA in
// seconds (Pulse "Response time" tile). Falls back to the default below when the
// flag/variant is absent, so behaviour matches the previous env-var default.
export const AGENT_RESPONSE_TIME_FLAG_NAME = 'agent_response_time';
export const DEFAULT_AGENT_RESPONSE_TIME_LIMIT_SECONDS = 6;
export const MENU_MODULE_ERROR_UI_FLAG_NAME = 'menu_module_error_ui';
// HITL-1699 kill switch. With this ON, the customer line sent from a cart
// line's red alert names what the menu module rejected ("Sorry, we're out of
// apple juice right now. …", "I'm having trouble adding your chicken strip
// sandwich."). OFF restores today's behaviour exactly: "I'm having trouble
// adding that item." for every menu-module error.
//
// ON/OFF ONLY — no variant payload.
export const MENU_MODULE_ERROR_NAMED_TTS_FLAG_NAME =
  'menu_module_error_named_tts';
// Offer a Retry button when POSMON has not returned the check event for a
// non-final order within the timeout (the order progress bar stays stuck below
// green until a check arrives). Carries a variant whose payload value is the
// timeout in seconds; the default below applies when the flag/variant carries
// no payload.
export const ORDER_CHECK_RETRY_FLAG_NAME = 'order_check_retry';
// The variant payload value is the timeout in seconds, used twice (HITL-1515):
// as the wait for the check event before the Retry banner appears, and again
// as the banner's auto-resend countdown — the order is resent automatically
// when the countdown ends without a click, at most once per stuck episode
// (see useOrderCheckRetry). One flag governs both; killing it removes the
// banner and the auto-send together.
export const DEFAULT_ORDER_CHECK_TIMEOUT_SECONDS = 10;
// The persistent cart-top Order Status Banner (HITL-1527): one strip showing
// the order pipeline state (sent/acked/confirmed/failed/stuck/rejected) with a
// live duration, for agent and AI orders, non-final and final. Subsumes the
// Retry alert's placement — with this flag OFF the plain Retry alert renders
// as before; the retry semantics themselves stay governed by the
// order_check_retry flag either way.
export const ORDER_STATUS_BANNER_FLAG_NAME = 'order_status_banner';
// Hide the POSLINK 7002 "Send Order is Pending" order_status error from agents
// and treat the order as still in progress: POSLINK drops the colliding
// request, but a follow-up send (usually <1s later) or the queued final order
// carries the full cart, so nothing is wrong with the items themselves
// (HITL-1417). Non-final orders only — the progress bar stays not-green and
// the order_check_retry banner still covers orders whose check never arrives.
export const MASK_ORDER_PENDING_ERROR_FLAG_NAME = 'mask_order_pending_error';
// Gates the whole secondary-ASR path: with this on, customer transcript lines
// are sourced from `asr_promoted_text` events instead of `text`/`final` frames,
// and the promoted icon is read off each event's own `metadata.source`. This is
// the AI-side flag (the secondary engine is running at all) — HITL-1497 removed
// the separate HITL-side opt-in, so one flag now governs both sides.
export const SECONDARY_ASR_ENABLED_FLAG_NAME = 'secondary_asr_enabled';
// Gates the whole in-screen issue-reporting path (HITL-1492 Pillar 3, Part A).
// ANDed with hitl_3_0 in selectIsIssueReportingEnabled, because the bottom action
// bar the button lives in only exists in MainPageLayout (Body/index.tsx) — but kept
// a separate flag so it is killable without killing HITL 3.0.
//
// ON/OFF ONLY — no variant payload is read. The taxonomy is hardcoded in
// src/constants/issueTypes.ts (HITL-1501).
export const ISSUE_REPORTING_FLAG_NAME = 'hitl_issue_reporting';

// HITL-1551 kill switch. With this ON, a staff intervention on a car whose cart
// has unmet REQUIRED constraints fills them from the brand menu and submits the
// final order to the Menu Module in the background, so staff get the order on
// their screen instead of nothing. OFF restores today's behaviour exactly: the
// intervention cancels and the incomplete items never leave the client.
//
// ON/OFF ONLY — no variant payload.
export const AUTOFILL_FINAL_ORDER_ON_INTERVENTION_FLAG_NAME =
  'autofill_final_order_on_intervention';

// HITL-1429. With this ON, accepting an AI post-readback upsell suggestion also
// adds the matching slash-command item to the agent cart (and resolves the
// prompt's `{total}` placeholder before it is spoken), instead of only speaking
// the line and leaving the agent to run the omnibar autoprompt as well — which
// made the customer hear the same upsell twice.
//
// ANDed with hitl_3_0 AND agent_cart in selectIsAISuggestionAutoAddEnabled: the
// suggestion card only exists in the HITL 3.0 Pulse pane, and the feature
// mutates the agent cart. Kept a separate flag so it is killable without
// killing either.
//
// ON/OFF ONLY — no variant payload. OFF restores today's behaviour exactly,
// including the raw `{total}` in the spoken line (the placeholder fix ships
// bundled here rather than as a standalone change).
export const AI_SUGGESTION_AUTO_ADD_FLAG_NAME = 'hitl_ai_suggestion_auto_add';

// HITL-1562 kill switch. With this ON, the auto-submit path — the
// post-car_exit timer expiring in autoSubmitHandler — autofills unmet
// REQUIRED constraints before submitting, the way a staff intervention
// already does. OFF keeps it byte-for-byte on today's path. Deliberately
// independent of the intervention flag above, so this rolls out (or dies)
// without touching the behaviour already live. (The Finish Order click needs
// no autofill — the button is disabled for invalid carts — and the
// kill-switch trigger is dormant; see HITL-1563/HITL-1567.)
//
// ON/OFF ONLY — no variant payload.
export const AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT_FLAG_NAME =
  'autofill_final_order_on_auto_submit';

// HITL-1607 kill switch. With this ON, the auto-submit paths (post-car_exit
// timer, kill switch) bypass sendOrder's isAIAutoMode guard, so an escalated
// car the agent never intercepted still gets its final order submitted at car
// exit (see selectAutoSubmitBypassesAIAutoMode for why the bypass is safe).
// OFF keeps today's behaviour exactly: the guard stays live on every auto
// submit, and that car's order is dropped.
//
// Separate from the autofill flag above because the risk profiles differ: the
// autofill fills a cart, this one authorises a SEND the guard used to block —
// this is the flag that stops the new sends without losing the fill behaviour.
//
// ON/OFF ONLY — no variant payload.
export const AUTO_SUBMIT_AI_AUTO_MODE_BYPASS_FLAG_NAME =
  'auto_submit_ai_auto_mode_bypass';

// HITL 7.0 (REL-8161). A set of console refinements layered on top of HITL 3.0:
// the AI status bar moves to full width, the Copilot card becomes a chip strip,
// the OmniBar moves into the centre pane, the transcript loses its bubbles, and
// the AI suggestion renders inline in the conversation.
//
// ANDed with hitl_3_0 in selectIsHitl7Enabled, and read ONLY through that
// selector. 7.0 is 3.0 plus deltas, never a UX of its own — every delta assumes
// the 3.0 layout is already on screen, so there is no standalone 7.0 console to
// fall back to. That is why the raw selector below it is NOT exported.
//
// Kept a separate flag so the whole set is killable without killing HITL 3.0.
//
// ON/OFF ONLY — no variant payload.
export const HITL_7_0_FLAG_NAME = 'hitl_7_0';

// HITL-1603. The single gate in front of the reworked centre pane (release
// 7.1.0 / REL-8164): one row per modifier group with its options in a drawer,
// replacing the chip grid that wraps five to a row and strips the vowels out
// of long option names. With this OFF the centre pane is today's, byte for
// byte.
//
// Read ONLY through selectIsHitl7CenterPaneEnabled, which ANDs it with
// hitl_3_0 AND hitl_7_0. Both are load-bearing: the pane is rendered from
// MainPageLayout, which only exists under 3.0, and the rows are laid out
// against the 7.0 centre pane - the OmniBar pinned at its foot, the status bar
// moved out above it - so with 7.0 off there is no pane of that shape to sit
// in.
//
// Kept a separate flag so the centre pane stays killable without killing HITL
// 3.0 or the rest of the 7.0 console refinements.
//
// ON/OFF ONLY - no variant payload.
//
// Retirement: see docs/TECH_DEBT.md. This flag, hitl_3_0 and hitl_7_0 all come
// out together once Fazoli's is moved to the brand menu.
export const HITL_7_CENTER_PANE_FLAG_NAME = 'hitl_7_center_pane';

// HITL-1604. The single gate in front of Supervised AI mode (release 7.3.0 /
// REL-8172): the Shadow / Intercept agent-mode enum, the header mode switch,
// and the rule that outside Intercept nothing the agent does reaches the
// customer or the POS. With this OFF the console is today's, byte for byte.
//
// Read ONLY through selectIsShadowModeEnabled, which ANDs it with hitl_3_0 AND
// hitl_7_0. Both are load-bearing: every surface the mode touches is rendered
// from MainPageLayout, which only exists under 3.0, and the mode switch sits in
// the 7.0 header beside the restaurant picker — with 7.0 off there is no header
// of that shape to put it in.
//
// Kept a separate flag so supervised mode is killable without killing HITL 3.0
// or the rest of the 7.0 console. Deliberately NOT ANDed with agent_cart: that
// flag gates publishing the agent cart to the AI (useAgentCartSync), which is
// outbound and a concern of Intercept alone, not of whether the mode exists.
//
// ON/OFF ONLY — no variant payload. Which mode the agent is in is agent state,
// not config; this flag only says whether the mode exists at all.
export const SHADOW_MODE_FLAG_NAME = 'hitl_shadow_mode';

// The repeated-slot panel: one panel in place of N identical single-pick
// groups (Baker's Dozen `bagel_choice_1..13`). Read ONLY through
// selectIsRepeatedSlotPanelEnabled, which ANDs it with the centre-pane gate —
// the panel replaces the 7.x rows, so with those off there is nothing to
// replace. Kept separate so it is killable per restaurant on its own.
// ON/OFF ONLY.
export const REPEATED_SLOT_PANEL_FLAG_NAME = 'hitl_repeated_slot_panel';

// HITL-1747 kill switch. With this ON, an item's built modifier tree is reused
// for later adds of the same item (and the AI's hypothesis re-adds) until the
// brand menu, 86 list or active meal periods change. A DQ burger builds ~154,500
// options, and rebuilding it on every AI update froze the console. OFF builds
// every add from scratch, exactly as before.
// ON/OFF ONLY.
export const MENU_ITEM_BUILD_CACHE_FLAG_NAME = 'hitl_menu_item_build_cache';

// HITL-1748 kill switch. With this ON, a brand-menu item is built with its own
// groups and their options only; each option's nested groups are built the
// first time something reads them (lazyOptionBuild.getOptionModifierGroups) or
// it is selected. A DQ burger drops from ~154,500 built options to a few
// hundred. OFF builds the whole tree up front, exactly as before.
// ON/OFF ONLY. Independent of hitl_menu_item_build_cache.
export const LAZY_OPTION_BUILD_FLAG_NAME = 'hitl_lazy_option_build';

// HITL-1749. With this ON, every answer line in a brand-menu item marked
// `informational_only: yes` (menu category*, restaurant info*, promotions*)
// becomes an `/info_*` slash command that speaks the curated answer. OFF adds
// no commands, exactly as before.
// Carries a JSON variant payload: the keys to exclude, keyed by a piece of
// the restaurant code, matched with contains (`{"dq_": {...}, "dairy_queen":
// {...}}`, each `{"exclude": ["sizes_info"]}`). Missing or unreadable payload
// excludes nothing.
export const INFO_SLASH_COMMANDS_FLAG_NAME = 'info_slash_commands';
